import { Component, type ReactNode } from "react";
import { focusRouteHeading } from "./focus-management";
import { publishPortalRenderFailure } from "./portal-error-reporting";
import {
  PORTAL_ROOT_BOUNDARY_ID,
  type PortalRenderFailureReporter,
} from "./route-error-model";

interface PortalRootErrorBoundaryProps {
  readonly reporter?: PortalRenderFailureReporter;
  readonly children: ReactNode;
}

interface PortalRootErrorBoundaryState {
  readonly failed: boolean;
}

interface PortalRootErrorFallbackProps {
  readonly onReload?: () => void;
}

function reloadPortal() {
  window.location.reload();
}

export function PortalRootErrorFallback({
  onReload = reloadPortal,
}: PortalRootErrorFallbackProps) {
  return (
    <main
      id="portal-root-content"
      className="portal-root-error"
      aria-labelledby="portal-root-error-title"
      aria-describedby="portal-root-error-detail"
    >
      <div className="portal-error-card">
        <p className="portal-kicker">Secure portal</p>
        <h1
          id="portal-root-error-title"
          tabIndex={-1}
          data-main-focus
          data-route-focus
        >
          We couldn’t open the portal.
        </h1>
        <p id="portal-root-error-detail">
          Your private information is safe. Reload the portal or return home. If
          this keeps happening, contact Whitehaven through an approved channel.
        </p>
        <div className="portal-error-actions">
          <button type="button" onClick={onReload}>
            Reload portal
          </button>
          <a href="/">Portal home</a>
        </div>
      </div>
    </main>
  );
}

export class PortalRootErrorBoundary extends Component<
  PortalRootErrorBoundaryProps,
  PortalRootErrorBoundaryState
> {
  override state: PortalRootErrorBoundaryState = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch() {
    publishPortalRenderFailure(
      {
        code: "PORTAL_ROOT_RENDER_FAILED",
        boundaryId: PORTAL_ROOT_BOUNDARY_ID,
        routeId: "unknown",
        retryCount: 0,
      },
      this.props.reporter,
    );

    this.scheduleHeadingFocus();
  }

  private pendingFocusFrame: number | undefined;

  private readonly scheduleHeadingFocus = () => {
    if (typeof window === "undefined") return;
    if (this.pendingFocusFrame !== undefined) {
      window.cancelAnimationFrame(this.pendingFocusFrame);
    }
    this.pendingFocusFrame = window.requestAnimationFrame(() => {
      this.pendingFocusFrame = undefined;
      focusRouteHeading();
    });
  };

  override componentWillUnmount() {
    if (typeof window !== "undefined" && this.pendingFocusFrame !== undefined) {
      window.cancelAnimationFrame(this.pendingFocusFrame);
    }
  }

  override render() {
    return this.state.failed ? (
      <PortalRootErrorFallback />
    ) : (
      this.props.children
    );
  }
}
